import { RunningPattern } from 'core/rtbCampaign/singlePage/common';
import {
  UpdateEventListener,
  FireableUpdateEventListener,
} from 'utils/UpdateEventListener';
import { AddonFeatureManager } from 'core';
import {
  RtbCampaignBasic,
  CampaignState,
  AdType,
  DeliverType,
} from 'core/rtbCampaign/RtbCampaign';
import { ColumnDefinition } from 'components/TableColumn/TableColumn';
import { Order, State } from 'core/order/Order';
import {
  formatPrice,
  formatPriceAny,
  formatPriceWithCurrency,
  getPriceValue,
} from 'helper/CurrencyHelper';
import { toast } from 'react-toastify';
import { CampaignListColumns, columns } from './CampaignListColumnSetting';
import {
  RtbCampaignManager,
  DefaultRtbCampaignManager,
} from 'core/rtbCampaign/RtbCampaignManager';
import { numberWithCommas } from 'utils/StringUtil';
import styles from './campaignList.module.scss';
import moment from 'moment';
import i18n from 'i18n';
import _ from 'lodash';
import {
  SortableList,
  AbstractSortableList,
} from 'containers/Common/AbstractSortableList';
import { SelectOptions } from 'components/commonType';
import { FilterMenuTabConfig } from 'components/FilterMenuTab/FilterMenuTab';
import { L1ObjectChannel } from 'core/l1Object/L1Object';
import {
  L1ObjectManager,
  DefaultL1ObjectManager,
} from 'core/l1Object/L1ObjectManager';
import { getCpc, getCtr } from 'helper/MetricsHelper';

const timeFormat = 'YYYY-MM-DD HH:mm:ss';
export interface CampaignListModel extends SortableList {
  readonly order: Order;
  readonly campaignList: Array<RtbCampaignBasic>;
  readonly state: CampaignListState;
  readonly event: UpdateEventListener<CampaignListModel>;
  readonly campaignStates: Array<string>;
  readonly canCreateCampaign: boolean;
  readonly canSplitCampaign: boolean;
  readonly showSplitBtn: boolean;
  readonly searchString: string;
  readonly lang: string;
  readonly canNotCreateMessage: string;
  readonly newCampaignPath: string;
  readonly editCampaignPath: string;
  readonly copyCampaignPath: string;
  readonly filterMenuTabConfigs: Array<FilterMenuTabConfig>;
  deleteCampaignIds: Array<number>;
  handleOnSelect(campaign): void;
  handleOnSelectAll(): void;
  handleRemoveSelect(): void;
  getColumnDefinition(columnName): ColumnDefinition;
  handleOnSearch(campaignName): void;
  handleOnStateFilterApply(stateDesList): void;
  activeCampaign(event): Promise<void>;
  deactiveCampaign(event): Promise<void>;
  allSelectedCampaignSameType(): boolean;
  selectUnknowAdTypeCampaign(): boolean;
  deleteCampaign(campaignIds): Promise<void>;
  canDeleteSelectedCampaigns(campaignIds: Array<number>): boolean;
  setVisibilityOfDeleteConfirmModal(show: boolean): void;
  getReportLink(campaign): string;
  getReportRangeNote(campaign): string;
  updateModelData(order: Order, campaignList: Array<RtbCampaignBasic>): void;
  updateViewModelData(): void;
  onSearchChange(searchString): void;
  getTabs(): SelectOptions[];
  getCampaignStatusDesData(campaign: any): {
    des: string;
    color: string;
  };
  getCampaignEffectiveStatusDesData(campaign: any): {
    des: string;
    color: string;
    extraInfo?: any;
  };
  isCampaignBudgetRemain(campaign): boolean;
}

export type CampaignListProps = {
  readonly model: CampaignListModel;
};

export enum CampaignListType {
  BASIC = 'basic',
  PERFORMANCE = 'performance',
}

export type CampaignListState = {
  readonly loading: boolean;
  readonly selectedCampaign: number[];
  readonly columnsToShow: CampaignListColumns[];
  readonly seletedTab: string;
  readonly viewModelData: any[];
  readonly summaryData: any;
  readonly selectedStateFilter: string[];
  readonly selectedEffectiveStatusFilter: string[];
  readonly showDeleteConfirmModal: boolean;
  readonly needUpdateViewModelData: boolean;
};

export const getEffectiveStatusDefaultColor = (effectiveStatus) => {
  const colorMap = {
    preapproved: 'success',
    pending_review: 'warning',
    pending_billing_info: 'warning',
    paused: 'black',
    campaign_paused: 'black',
    deactive: 'black',
    in_process: 'whiteTheme4',
    disapproved: 'danger',
    archived: 'danger',
    with_issues: 'danger',
    budget_remain: 'danger',
    deleted: 'danger',
  };

  const key = effectiveStatus.toLowerCase();
  return colorMap[key] ? colorMap[key] : 'theme1';
};
export class DefaultCampaignListModel
  extends AbstractSortableList
  implements CampaignListModel
{
  event: FireableUpdateEventListener<CampaignListModel> =
    new FireableUpdateEventListener<CampaignListModel>();
  selectedCampaign: number[] = [];
  filteredViewModelData: any[] = [];
  columnsToShow: Array<CampaignListColumns>;
  seletedTab: string = CampaignListType.BASIC;
  campaignStates: string[] = [];
  campaignEffectiveStatus: string[] = [];
  searchString: string;
  selectedStateFilter: string[] = [];
  selectedEffectiveStatusFilter: string[] = [];
  loading: boolean = false;
  showDeleteConfirmModal: boolean = false;
  lang: string = i18n.language;
  modelDeleteCampaignIds: number[] = [];
  needUpdateViewModelData: boolean = false;
  summaryData: any = {};
  isChannelAllowed: boolean;

  constructor(
    public order: Order,
    public campaignList: any[],
    protected addonFeatureManager: AddonFeatureManager,
    public onSearchChange: (search: string) => void,
    defaultSearchString: string | null,
    private budgetBalance: number,
    private inactivePmpSpaces: SelectOptions[],
    private refetchCampaignList: () => Promise<void>,
    public manager: RtbCampaignManager = new DefaultRtbCampaignManager(),
    l1ObjectManager: L1ObjectManager = new DefaultL1ObjectManager(),
  ) {
    super('id', 'desc');
    this.columnsToShow = columns;
    this.searchString = defaultSearchString ? defaultSearchString : '';
    this.isChannelAllowed = l1ObjectManager.isChannelAllowed(
      L1ObjectChannel.RTB,
      addonFeatureManager,
    );
    this.updateViewModelData();
  }

  get canNotCreateMessage(): string {
    const canNotCreateState = [
      State.NOT_APPROVE,
      State.REJECT,
      State.SETTLE,
      State.SETTLED,
      State.CHANGE_PENDING,
    ];
    const state = this.order.state;
    if (canNotCreateState.includes(state)) {
      return i18n.t<string>('orderDetail.labels.orderStateCannotCreate');
    }

    const isEnd = moment(this.order.endDate).isBefore(moment().startOf('day'));
    if (isEnd) {
      return i18n.t<string>('orderDetail.labels.isEndCannotCreate');
    }

    if (this.budgetBalance <= 0) {
      return i18n.t<string>('orderDetail.labels.lessThanBudgetMinimum', {
        budget: 0,
      });
    }

    return '';
  }

  getCanNotEditMessage(): string {
    const canNotEditState = [
      State.NOT_APPROVE,
      State.REJECT,
      State.SETTLE,
      State.SETTLED,
      State.CHANGE_PENDING,
    ];
    const state = this.order.state;
    if (canNotEditState.includes(state)) {
      return i18n.t<string>('orderDetail.labels.orderStateCannotEdit');
    }

    return '';
  }

  getCanNotCopyMessage(campaign: any): string {
    const canNotCreateState = [
      State.NOT_APPROVE,
      State.REJECT,
      State.SETTLE,
      State.SETTLED,
      State.CHANGE_PENDING,
    ];
    const state = this.order.state;
    if (canNotCreateState.includes(state)) {
      return i18n.t<string>('orderDetail.labels.orderStateCannotCreate');
    }

    const isEnd = moment(this.order.endDate).isBefore(moment().startOf('day'));
    if (isEnd) {
      return i18n.t<string>('orderDetail.labels.isEndCannotCreate');
    }

    /*  關鍵字活動無限走期，最低訂單可支配預算為一天日預算
     *  總預算模式，最低訂單可支配預算需大於等於總預算
     *  日預算模式，最低訂單可支配預算需大於等於日預算*走期天數
     */

    switch (campaign.adType) {
      case AdType.KEYWORD:
      case AdType.SMART_KEYWORD:
      case AdType.MAX_CVR_KEYWORD:
      case AdType.OUTBOUND_KEYWORD:
        if (campaign.runningPattern === RunningPattern.UNLIMITED) {
          if (this.budgetBalance < campaign.dailyTargetBudget) {
            return i18n.t<string>('campaignList.labels.canCopyNeedMore', {
              parent: 'Order',
              budget: formatPriceWithCurrency(
                this.order.currency,
                campaign.dailyTargetBudget - this.budgetBalance,
              ),
            });
          }
        } else if (campaign.runningPattern === RunningPattern.LIMITED) {
          if (this.budgetBalance < campaign.budget) {
            return i18n.t<string>('campaignList.labels.canCopyNeedMore', {
              parent: 'Order',
              budget: formatPriceWithCurrency(
                this.order.currency,
                campaign.budget - this.budgetBalance,
              ),
            });
          }
        }
        break;
      case AdType.DISPLAY:
      case AdType.SHOP:
      case AdType.RECOMMEND:
        if (this.budgetBalance < campaign.budget) {
          return i18n.t<string>('campaignList.labels.canCopyNeedMore', {
            parent: 'Order',
            budget: formatPriceWithCurrency(
              this.order.currency,
              campaign.budget - this.budgetBalance,
            ),
          });
        }
        break;
      default:
        break;
    }

    return '';
  }

  getCanNotSplitMessage(campaign: any): string {
    const canNotCreateState = [
      State.NOT_APPROVE,
      State.REJECT,
      State.SETTLE,
      State.SETTLED,
      State.CHANGE_PENDING,
    ];
    const state = this.order.state;
    if (canNotCreateState.includes(state)) {
      return i18n.t<string>('orderDetail.labels.orderStateCannotCreate');
    }

    const isEnd = moment(this.order.endDate).isBefore(moment().startOf('day'));
    if (isEnd) {
      return i18n.t<string>('orderDetail.labels.isEndCannotCreate');
    }

    const targetBudget = campaign.budget;
    const expectSpent = campaign.spents * 1.1;
    const minCampaignBudgetPerDay = this.order.campaignConstraint.budgetMinimum;
    const startDate = moment(campaign.startDate).startOf('day');
    const today = moment().startOf('day');
    const isStart = today.isAfter(startDate);
    const endDate = moment(campaign.endDate);
    const scheduleDateCount =
      endDate.diff(isStart ? today : startDate, 'days') + 1;
    const minBudgetFromNow = minCampaignBudgetPerDay * scheduleDateCount;
    const firstCampaignBudget = isStart
      ? Math.max(
          expectSpent,
          minCampaignBudgetPerDay * today.diff(startDate, 'days'),
        ) + minBudgetFromNow
      : minBudgetFromNow;
    const secondCampaignBudget = minBudgetFromNow;
    const minBudgetFor2Campaign = firstCampaignBudget + secondCampaignBudget;
    const isBelowMinBudget = targetBudget < minBudgetFor2Campaign;
    if (isBelowMinBudget) {
      return i18n.t<string>('campaignList.labels.canSplitMinimum', {
        budget: formatPriceWithCurrency(
          this.order.currency,
          minBudgetFor2Campaign,
        ),
      });
    }

    return '';
  }

  get deleteCampaignIds(): Array<number> {
    return this.modelDeleteCampaignIds;
  }

  set deleteCampaignIds(campaignIds) {
    this.modelDeleteCampaignIds = campaignIds;
  }

  get state(): CampaignListState {
    return {
      selectedCampaign: this.selectedCampaign,
      columnsToShow: this.columnsToShow,
      seletedTab: this.seletedTab,
      needUpdateViewModelData: this.needUpdateViewModelData,
      viewModelData: this.filteredViewModelData,
      summaryData: this.summaryData,
      selectedStateFilter: this.selectedStateFilter,
      selectedEffectiveStatusFilter: this.selectedEffectiveStatusFilter,
      loading: this.loading,
      showDeleteConfirmModal: this.showDeleteConfirmModal,
    };
  }

  get canCreateCampaign(): boolean {
    const canNotCreateState = [
      State.NOT_APPROVE,
      State.REJECT,
      State.SETTLE,
      State.SETTLED,
      State.CHANGE_PENDING,
    ];
    const state = this.order.state;
    const isEnd = moment(this.order.endDate).isBefore(moment().startOf('day'));
    const noBudget = this.budgetBalance <= 0;
    return (
      !(canNotCreateState.includes(state) || isEnd || noBudget) &&
      this.isChannelAllowed
    );
  }

  get canSplitCampaign(): boolean {
    const canNotSplitState = [
      State.NOT_APPROVE,
      State.REJECT,
      State.SETTLE,
      State.SETTLED,
      State.CHANGE_PENDING,
    ];
    const state = this.order.state;
    const isEnd = moment(this.order.endDate).isBefore(moment().startOf('day'));
    return (
      !(canNotSplitState.includes(state) || isEnd) && this.isChannelAllowed
    );
  }

  get showSplitBtn(): boolean {
    return true;
  }

  get newCampaignPath() {
    return 'select-adType';
  }

  get editCampaignPath() {
    return 'edit';
  }

  get copyCampaignPath() {
    return 'copy';
  }

  get filterMenuTabConfigs(): FilterMenuTabConfig[] {
    return [
      {
        filterType: i18n.t<string>('l2ObjectList.labels.statusFilter'),
        menuTitle: i18n.t<string>('l2ObjectList.labels.statusFilterMenuTitle'),
        tag: i18n.t<string>('l2ObjectList.labels.statusTag'),
        selectedValues: this.state.selectedStateFilter,
        options: this.campaignStates,
        applyMethod: this.handleOnStateFilterApply,
      },
      {
        filterType: i18n.t<string>('l2ObjectList.labels.deliveryFilter'),
        menuTitle: i18n.t<string>(
          'l2ObjectList.labels.deliveryFilterMenuTitle',
        ),
        tag: i18n.t<string>('l2ObjectList.labels.deliveryTag'),
        selectedValues: this.state.selectedEffectiveStatusFilter,
        options: this.campaignEffectiveStatus,
        applyMethod: this.handleOnEffectiveStatusFilterApply,
      },
    ];
  }

  getTabs() {
    return Object.values(CampaignListType).map((tab) => ({
      label: i18n.t<string>(`campaignList.tabs.${tab}`),
      value: tab,
    }));
  }

  sortComparator = (field: string | undefined, dataA, dataB, order) => {
    const dataAToCompare = field ? dataA[field] : dataA;
    const dataBToCompare = field ? dataB[field] : dataB;
    if (order === 'desc') {
      return dataAToCompare < dataBToCompare ? 1 : -1;
    }

    return dataAToCompare > dataBToCompare ? 1 : -1;
  };

  getSortFunc = (dataField?: string) => {
    return _.partial(this.sortComparator, dataField);
  };

  get nameColumnAdditional() {
    return {
      events: {
        onClick: () => {
          // This is intentional
        },
      },
      sort: true,
      onSort: this.handleSort,
      sortFunc: (dataA, dataB, order) => {
        const compareListId = _.get(dataA, 'listId', 0)
          .toString()
          .localeCompare(_.get(dataB, 'listId', 0).toString());
        if (compareListId === 0) {
          return 0;
        }
        if (order === 'desc') {
          return compareListId > 0 ? -1 : 1;
        } else {
          return compareListId < 0 ? -1 : 1;
        }
      },
    };
  }

  get stateColumnAdditional() {
    return {
      sort: true,
      onSort: this.handleSort,
      sortFunc: (dataA, dataB, order) => {
        const stateDes = [
          i18n.t<string>('campaignList.labels.activateState'),
          i18n.t<string>('campaignList.labels.stoppingState'),
          i18n.t<string>('campaignList.labels.deactivateState'),
          i18n.t<string>('campaignList.labels.deleteState'),
        ];
        const dataAToCompare = stateDes.indexOf(dataA['des']);
        const dataBToCompare = stateDes.indexOf(dataB['des']);
        if (order === 'desc') {
          return dataAToCompare > dataBToCompare ? 1 : -1;
        } else {
          return dataAToCompare < dataBToCompare ? 1 : -1;
        }
      },
    };
  }

  get deliveryColumnAdditional() {
    return {
      sort: true,
      onSort: this.handleSort,
      sortFunc: (dataA, dataB, order) => {
        const stateDes = [
          i18n.t<string>('campaignList.labels.budgetRemainState'),
          i18n.t<string>('campaignList.labels.runningState'),
          i18n.t<string>('campaignList.labels.stoppingState'),
          i18n.t<string>('campaignList.labels.deactivateState'),
          i18n.t<string>('campaignList.labels.noCreativesState'),
          i18n.t<string>('campaignList.labels.notStartState'),
          i18n.t<string>('campaignList.labels.endState'),
          i18n.t<string>('campaignList.labels.deleteState'),
        ];
        const dataAToCompare = stateDes.indexOf(dataA['des']);
        const dataBToCompare = stateDes.indexOf(dataB['des']);
        if (order === 'desc') {
          return dataAToCompare > dataBToCompare ? 1 : -1;
        } else {
          return dataAToCompare < dataBToCompare ? 1 : -1;
        }
      },
    };
  }

  get scheduleColumnAdditional() {
    return {
      sort: true,
      onSort: this.handleSort,
      sortFunc: (dataA, dataB, order) => {
        if (!dataA || !dataB) {
          return !dataA ? -1 : 1;
        }

        if (order === 'desc') {
          return moment(dataA.start).isBefore(moment(dataB.start)) ? 1 : -1;
        }

        return moment(dataB.start).isBefore(moment(dataA.start)) ? 1 : -1;
      },
    };
  }

  getCommonColumnAdditional(columnName?: string) {
    return {
      sort: true,
      onSort: this.handleSort,
      sortFunc: this.getSortFunc(columnName),
    };
  }

  getColumnDefinition(columnName): ColumnDefinition {
    let additional = {};

    let customLabel;
    switch (columnName) {
      case CampaignListColumns.NAME:
        additional = this.nameColumnAdditional;
        break;
      case CampaignListColumns.STATE:
        additional = this.stateColumnAdditional;
        break;
      case CampaignListColumns.DELIVERY:
        additional = this.deliveryColumnAdditional;
        break;
      case CampaignListColumns.CREATIVE:
        additional = this.getCommonColumnAdditional();
        break;
      case CampaignListColumns.SCHEDULE:
        additional = this.scheduleColumnAdditional;
        break;
      case CampaignListColumns.PROGRESS:
        additional = this.getCommonColumnAdditional('executeRate');
        break;
      case CampaignListColumns.BUDGET:
        customLabel = i18n.t<string>('campaignList.headers.budgetColumn');
        additional = this.getCommonColumnAdditional('budget');
        break;
      case CampaignListColumns.EDITBTNS:
        additional = {
          text: '',
        };
        break;
      case CampaignListColumns.IMPRES:
      case CampaignListColumns.CLICKS:
      case CampaignListColumns.CTR:
      case CampaignListColumns.ROAS:
        additional = this.getCommonColumnAdditional('value');
        break;
      default:
        break;
    }

    return this.columnDefinition(columnName, customLabel, additional);
  }

  columnDefinition(columnName, customLabel, additional = {}): ColumnDefinition {
    const columnClassGetter = () => {
      return styles[columnName];
    };

    return {
      sort: false,
      text: customLabel
        ? customLabel
        : i18n.t<string>(`campaignList.headers.${columnName}`),
      dataField: columnName,
      classes: columnClassGetter,
      headerClasses: columnClassGetter,
      ...additional,
    };
  }

  getReportLink(campaign) {
    const startMoment = moment(campaign.startDate).startOf('day');
    let endMoment = moment(campaign.endDate).endOf('day');
    // 限制報表查詢範圍最大為 92 天
    if (endMoment.diff(startMoment, 'days') > 92) {
      endMoment = startMoment.clone().add(92, 'days').endOf('day');
    }
    const from = encodeURIComponent(startMoment.format('YYYY-MM-DD HH:mm:ss'));
    const to = encodeURIComponent(endMoment.format('YYYY-MM-DD HH:mm:ss'));
    const dimension = 'l2ChannelId';
    return `/reports/performance?dimension=${dimension}&from=${from}&to=${to}&${dimension}=${campaign.id}`;
  }

  getReportRangeNote(campaign): string {
    const startMoment = moment(campaign.startDate).startOf('day');
    let endMoment = moment(campaign.endDate).endOf('day');
    // 限制報表查詢範圍最大為 92 天
    if (endMoment.diff(startMoment, 'days') > 92) {
      return '( 僅顯示 92 天 )';
    }
    return '';
  }

  getCampaignStatusDesData = (campaign) => {
    let des = '';
    let color;
    let extraInfo;
    let hintData;
    switch (campaign.state) {
      case CampaignState.DEACTIVATE:
        des = i18n.t<string>('campaignList.labels.deactivateState');
        color = 'black';
        break;
      case CampaignState.DELETE:
        des = i18n.t<string>('campaignList.labels.deleteState');
        color = 'danger';
        break;
      case CampaignState.ACTIVATE:
        des = i18n.t<string>('campaignList.labels.activateState');
        break;
      default:
        des = i18n.t<string>('campaignList.labels.stoppingState');
        color = 'whiteTheme4';
        break;
    }

    return {
      des,
      color,
      extraInfo,
      hintData,
    };
  };

  getCampaignEffectiveStatusDesData = (campaign) => {
    const bidPriceSettingData = this.order.campaignBidPrice;
    const adTypeBidPriceSetting = bidPriceSettingData.find(
      (data) => data.type === campaign.adType,
    );
    const bidFloor = _.get(
      adTypeBidPriceSetting,
      `bidFloor.${_.camelCase(this.manager.getOptimizeDes(campaign.optimize))}`,
    );
    if (
      !_.isNil(campaign.bidPrice) &&
      bidFloor &&
      bidFloor > campaign.bidPrice
    ) {
      return {
        des: i18n.t<string>('campaignList.labels.bidPriceLowerThanFloor'),
        color: 'danger',
      };
    }
    let des = _.startCase(_.lowerCase(campaign.effectiveStatus));
    let color = getEffectiveStatusDefaultColor(campaign.effectiveStatus);
    let extraInfo;
    const now = moment();
    switch (campaign.effectiveStatus) {
      case 'IN_PROGRESS':
        des = i18n.t<string>('campaignList.labels.inProgressState');
        color = 'inProgress';
        break;
      case 'PAUSED':
      case 'CAMPAIGN_GROUP_PAUSED':
        if (campaign.budget - campaign.spents > 0) {
          des = i18n.t<string>('campaignList.labels.budgetRemainState');
          color = 'danger';
        } else {
          des = i18n.t<string>('campaignList.labels.deactivateState');
          color = 'black';
        }
        if (campaign.effectiveStatus === 'CAMPAIGN_GROUP_PAUSED') {
          extraInfo = 'Campaign Group Paused';
        }
        break;
      case 'DELETED':
      case 'ARCHIVED':
        des = i18n.t<string>('campaignList.labels.deleteState');
        color = 'danger';
        break;
      case 'ACTIVE':
        if (moment(campaign.startDate).isAfter(now)) {
          des = i18n.t<string>('campaignList.labels.notStartState');
          color = 'outOfStock';
        } else if (moment(campaign.endDate).isBefore(now)) {
          des = i18n.t<string>('campaignList.labels.endState');
          color = 'light';
        } else if (
          campaign.additionalInfo &&
          campaign.additionalInfo.creativeAmount.enableCount === 0
        ) {
          des = i18n.t<string>('campaignList.labels.noCreativesState');
          color = 'black';
        } else {
          des = i18n.t<string>('campaignList.labels.runningState');
        }
        break;
      default:
        break;
    }

    return {
      des,
      color,
      extraInfo,
    };
  };

  isCampaignBudgetRemain = (campaign) => {
    return (
      ['PAUSED', 'CAMPAIGN_GROUP_PAUSED'].includes(campaign.effectiveStatus) &&
      campaign.budget - campaign.spents > 0
    );
  };

  getProgress(campaign) {
    const campaignProgress = this.getCampaignProgressRate(campaign);
    const executeRate = campaignProgress.executeRate;
    const predictRate = Math.min(campaignProgress.predictRate, 1);
    const warnignLine = campaign.budget * 1.1;
    const discrepancy = predictRate - executeRate;
    const runningPattern = campaign.runningPattern
      ? campaign.runningPattern
      : null;
    return {
      spents: `${this.order.currency} ${numberWithCommas(_.floor(campaign.spents, 2).toFixed(2))}`,
      budget: `${this.order.currency} ${numberWithCommas(_.floor(campaign.budget, 2).toFixed(2))}`,
      executeRate,
      predictRate,
      danger: campaign.spents > warnignLine,
      deepWarning: discrepancy > 0.03,
      warning: discrepancy > 0.01 && discrepancy <= 0.03,
      adType: campaign.adType,
      runningPattern: runningPattern,
    };
  }

  getDistributeDes(dailyTargetBudget) {
    return dailyTargetBudget && dailyTargetBudget > 0
      ? i18n.t<string>('campaignList.labels.budgetDailyDistribution', {
          currency: this.order.currency,
          budget: formatPrice(this.order.currency, dailyTargetBudget),
        })
      : i18n.t<string>('campaignList.labels.budgetSchedule');
  }

  getPriceModelDes(priceModel) {
    return i18n.t<string>(`campaign.labels.${priceModel}`);
  }

  getLimitationsToShow(campaign) {
    let limitations = _.get(campaign, 'additionalInfo.limitations', {});
    if (
      [
        AdType.KEYWORD,
        AdType.SMART_KEYWORD,
        AdType.SHOP,
        AdType.MAX_CVR_KEYWORD,
      ].includes(campaign.adType)
    ) {
      limitations = {
        include: limitations.include,
      };
    }
    return this.manager.getLimitationSummaryData(limitations);
  }

  getDeliverTypeDesc(deliverType) {
    if (deliverType === DeliverType.STANDARD) {
      return i18n.t<string>('campaignList.labels.deliverStandard');
    }

    return i18n.t<string>('campaignList.labels.deliverAsap');
  }

  getViewModelData() {
    this.campaignStates = [];
    const viewModelData: Array<any> = this.campaignList.map((campaign) => {
      const stateData = this.getCampaignStatusDesData(campaign);
      const effectiveData = this.getCampaignEffectiveStatusDesData(campaign);
      this.campaignStates = _.uniq(
        _.concat(this.campaignStates, stateData.des),
      );
      this.campaignEffectiveStatus = _.uniq(
        _.concat(this.campaignEffectiveStatus, effectiveData.des),
      );
      const listId = campaign.id;
      const campaignData: any = {
        listId,
        pmp: _.get(campaign, 'additionalInfo.pmp'),
        id: campaign.id,
        adType: campaign.adType,
        spents: campaign.spents,
        budget: campaign.budget,
        dailyTargetBudget: campaign.dailyTargetBudget,
        priceModel: campaign.priceModel,
        optimize: campaign.optimize,
        bidPrice: campaign.bidPrice,
        impres: campaign.impres,
        startDate: campaign.startDate,
        endDate: campaign.endDate,
        state: campaign.state,
        effectiveStatus: campaign.effectiveStatus,
        stateColumn: stateData,
        deliveryColumn: effectiveData,
        additionalInfo: campaign.additionalInfo,
        runningPattern: campaign.runningPattern,
        selectPattern: campaign.selectPattern,
        budgetType: campaign.budgetType,
        bidMode: campaign.bidMode,
        nameColumn: {
          name: campaign.name,
          listId,
          typeDes: this.manager.getCampaignTypeDes(campaign.adType),
        },
        creativeColumn: _.get(
          campaign.additionalInfo,
          'creativeAmount.enableCount',
          0,
        ),
        scheduleColumn: {
          start: moment(campaign.startDate).format(timeFormat),
          end: moment(campaign.endDate).format(timeFormat),
          runningPattern: campaign.runningPattern ?? null,
        },
        progressColumn: this.getProgress(campaign),
        budgetColumn: {
          budget: campaign.budget,
          dailyTargetBudget: campaign.dailyTargetBudget,
          budgetDes: formatPriceWithCurrency(
            this.order.currency,
            campaign.budget,
          ),
          distributeDes: this.getDistributeDes(campaign.dailyTargetBudget),
          deliverTypeDes: this.getDeliverTypeDesc(campaign.deliverType),
          adType: campaign.adType,
          budgetType: campaign.budgetType ?? null,
          runningPattern: campaign.runningPattern ?? null,
        },
        priceColumn: {
          priceModel: this.getPriceModelDes(campaign.priceModel),
          type: campaign.optimize,
          bidPriceDes: `${this.order.currency} ${numberWithCommas(campaign.bidPrice)}`,
          bidPrice: campaign.bidPrice,
          adType: campaign.adType,
          bidMode: campaign.bidMode,
        },
        limitationColumn: this.getLimitationsToShow(campaign),
        impresColumn: {
          value: campaign.impres,
          desc: numberWithCommas(campaign.impres),
        },
        clicksColumn: {
          value: campaign.clicks,
          desc: numberWithCommas(campaign.clicks),
        },
        roasColumn: {
          value: campaign.roas,
          desc: numberWithCommas((campaign.roas ?? 0).toFixed(2)),
        },
        cpcColumn: { value: getCpc(campaign.spents, campaign.clicks) },
        ctrColumn: { value: getCtr(campaign.impres, campaign.clicks) },
      };

      campaignData.canNotCopyMessage = this.getCanNotCopyMessage(campaignData);
      campaignData.canNotSlitMessage = this.getCanNotSplitMessage(campaignData);
      campaignData.canNotEditMessage = this.getCanNotEditMessage();
      return campaignData;
    });

    this.summaryData = {
      id: 0,
      listId: 'summaryRow',
      ...this.getSummaryData(this.campaignList),
    };

    return viewModelData;
  }

  handleOnSelect = (campaign) => {
    const objectId = campaign.id;
    const selectedArray: number[] = this.selectedCampaign;
    if (selectedArray.indexOf(objectId) > -1) {
      _.remove(selectedArray, (id) => id === objectId);
    } else {
      selectedArray.push(objectId);
    }

    this.updateState();
  };

  handleOnSelectAll = () => {
    const selectedArrays = [...this.selectedCampaign];
    if (selectedArrays.length === this.filteredViewModelData.length) {
      this.selectedCampaign = [];
    } else {
      this.selectedCampaign = this.filteredViewModelData.map(
        (viewModelData) => viewModelData.id,
      );
    }
    this.updateState();
  };

  handleOnSearch = (searchString) => {
    this.searchString = searchString;
    this.onSearchChange(searchString);
    this.updateViewModelData();
  };

  handleOnStateFilterApply = (stateDesList) => {
    this.selectedStateFilter = [...stateDesList];
    this.updateViewModelData();
  };

  handleOnEffectiveStatusFilterApply = (effectiveStatusDesList) => {
    this.selectedEffectiveStatusFilter = [...effectiveStatusDesList];
    this.updateViewModelData();
  };

  updateViewModelData = () => {
    this.needUpdateViewModelData = false;
    const allViewModelData = this.getViewModelData();
    this.filteredViewModelData = _.filter(allViewModelData, (viewModelData) => {
      const campaign = this.campaignList.find(
        (campaign) => campaign.id === viewModelData.id,
      );
      if (!campaign) {
        return false;
      }
      const stateIsMatch =
        (this.selectedStateFilter.length === 0 &&
          viewModelData.state !== CampaignState.DELETE) ||
        this.selectedStateFilter.includes(viewModelData.stateColumn.des);
      const effecitveStatuseIsMatch =
        this.selectedEffectiveStatusFilter.length === 0 ||
        this.selectedEffectiveStatusFilter.includes(
          viewModelData.deliveryColumn.des,
        );
      const nameIsMatch = viewModelData.nameColumn.name
        .toLowerCase()
        .includes(this.searchString.toLowerCase());
      const listIdIsMatch = viewModelData.listId
        ? viewModelData.listId.toString().includes(this.searchString)
        : false;
      return (
        (nameIsMatch || listIdIsMatch) &&
        stateIsMatch &&
        effecitveStatuseIsMatch
      );
    });
    const selectedObjects = _.intersectionWith(
      this.campaignList,
      this.filteredViewModelData,
      (campaign, viewModel) => campaign.id === viewModel.id,
    );
    this.summaryData = {
      id: 0,
      listId: 'summaryRow',
      ...this.getSummaryData(selectedObjects),
    };

    this.updateState();
  };

  getSummaryData = (campaignList: Array<any>) => {
    const clickSum = campaignList.reduce<number>(
      (partial, campaign) => partial + campaign.clicks,
      0,
    );
    const spentsSum = campaignList.reduce<number>(
      (partial, campaign) =>
        partial + getPriceValue(this.order.currency, campaign.spents),
      0,
    );
    const impresSum = campaignList.reduce<number>(
      (partial, campaign) => partial + campaign.impres,
      0,
    );
    return {
      nameColumn: i18n.t<string>('campaignList.labels.campaignCount', {
        count: campaignList.length,
      }),
      budgetColumn: i18n.t<string>('campaignList.labels.campaignBudget', {
        number: formatPriceWithCurrency(
          this.order.currency,
          campaignList.reduce<number>(
            (partial, campaign) => partial + campaign.budget,
            0,
          ),
        ),
      }),
      impresColumn: numberWithCommas(impresSum),
      clicksColumn: numberWithCommas(clickSum),
      spents: numberWithCommas(getPriceValue(this.order.currency, spentsSum)),
      cpcColumn: formatPriceAny(
        clickSum === 0 ? 0.0 : _.floor(spentsSum / clickSum, 2),
      ),
      ctrColumn: `${impresSum === 0 ? 0.0 : _.floor((clickSum / impresSum) * 100, 2).toFixed(2)}%`,
    };
  };

  getCampaignProgressRate = ({ budget, spents, startDate, endDate }) => {
    const startTime = moment(startDate).startOf('day');
    const endTime = moment(endDate).startOf('day');
    const now = moment().startOf('day');
    const isStart = moment().isSameOrAfter(moment(startDate));
    const isEnd = moment().isAfter(moment(endDate));

    let predictRate;
    if (!isStart) {
      predictRate = 0;
    } else if (isEnd) {
      predictRate = 1;
    } else {
      predictRate =
        (now.diff(startTime, 'day') + 1) / (endTime.diff(startTime, 'day') + 1);
    }

    const canCalRate = budget !== 0;
    if (canCalRate) {
      return { executeRate: spents / budget, predictRate };
    }
    return { executeRate: 0, predictRate: 0 };
  };

  handleRemoveSelect = () => {
    this.selectedCampaign = [];
    this.updateState();
  };

  activeCampaign = async (event): Promise<void> => {
    event && event.stopPropagation();
    await this.excuteCampaignAction(
      'activate',
      i18n.t<string>('campaignList.labels.activeSuccess'),
    );
  };

  deactiveCampaign = async (event): Promise<void> => {
    event && event.stopPropagation();
    await this.excuteCampaignAction(
      'deactivate',
      i18n.t<string>('campaignList.labels.deactiveSuccess'),
    );
  };

  generateUpdateStatePayload = (selectedObjects: number[]) => {
    const payload = selectedObjects.map((objectId) => {
      return {
        l2ChannelId: objectId,
      };
    });
    return payload;
  };

  excuteCampaignAction = async (
    action: 'activate' | 'deactivate',
    successMessage,
  ) => {
    this.updateLoading(true);
    const updateStatePayload = [
      ...this.generateUpdateStatePayload(this.selectedCampaign),
    ];
    try {
      await this.manager.updateCampaignState(updateStatePayload, action);
      await this.refetchCampaignList();
      this.selectedCampaign = [];
      toast.success(successMessage);
    } catch (e) {
      e instanceof Error && toast.error(e.message);
    }
    this.updateLoading(false);
  };

  deleteCampaign = async (campaignIds: Array<number>): Promise<void> => {
    this.updateLoading(true);
    try {
      await this.manager.deleteCampaigns(campaignIds);
      await this.refetchCampaignList();
      this.selectedCampaign = [];
      toast.success(i18n.t<string>('campaignList.labels.deleteSuccess'));
    } catch (e) {
      e instanceof Error && toast.error(e.message);
    }
    this.updateLoading(false);
  };

  allSelectedCampaignSameType() {
    if (this.selectedCampaign.length === 0) {
      return true;
    }

    const types = _.uniq(
      _.filter(
        this.campaignList,
        (campaign) =>
          campaign.id !== undefined &&
          this.selectedCampaign.indexOf(campaign.id) > -1,
      ).map((campaign) => campaign.adType),
    );
    return types.length === 1;
  }

  selectUnknowAdTypeCampaign() {
    if (this.selectedCampaign.length === 0) {
      return false;
    }

    const selectCampaignList = _.filter(
      this.campaignList,
      (campaign) =>
        campaign.id !== undefined &&
        this.selectedCampaign.indexOf(campaign.id) > -1,
    );
    return selectCampaignList.filter((campaign) => !campaign.adType).length > 0;
  }

  canDeleteSelectedCampaigns(campaignIds: Array<number>) {
    if (campaignIds.length === 0) {
      return false;
    }

    const canDeleteCampaigns = _.filter(this.campaignList, (campaign) => {
      const selected =
        campaign.id !== undefined && campaignIds.indexOf(campaign.id) > -1;
      if (!selected) {
        return false;
      }
      return (
        _.get(campaign, 'additionalInfo.creativeAmount.bindingCount', 0) === 0
      );
    });

    return canDeleteCampaigns.length === campaignIds.length;
  }

  setVisibilityOfDeleteConfirmModal(show: boolean) {
    this.showDeleteConfirmModal = show;
    this.updateState();
  }

  updateModelData(order: Order, campaignList: Array<RtbCampaignBasic>) {
    this.order = order;
    this.campaignList = campaignList;
    this.needUpdateViewModelData = true;
  }

  updateLoading(loading: boolean) {
    this.loading = loading;
    this.updateState();
  }

  updateState() {
    this.event.fireEvent(this);
  }
}
